/**
 * Cleveland HeartLab Reference Ranges
 * Based on the Cardiometabolic Report reference values
 */

export interface ReferenceRange {
  optimal: { min?: number; max?: number };
  moderate?: { min?: number; max?: number };
  high: { min?: number; max?: number };
  unit: string;
  targetValue?: number; // For chart reference lines
  displayRange?: string; // For display purposes
}

export const CLEVELAND_HEARTLAB_RANGES: Record<string, ReferenceRange> = {
  // LIPIDS
  totalCholesterol: {
    optimal: { max: 200 },
    high: { min: 200 },
    unit: 'mg/dL',
    displayRange: '<200',
  },
  ldlCholesterol: {
    optimal: { max: 100 },
    moderate: { min: 100, max: 129 },
    high: { min: 130 },
    unit: 'mg/dL',
    targetValue: 100,
    displayRange: '<100 (optimal), 100-129 (moderate), ≥130 (high)',
  },
  hdlCholesterol: {
    optimal: { min: 40 },
    high: { max: 40 },
    unit: 'mg/dL',
    targetValue: 60,
    displayRange: '≥40 (optimal), >60 (best), <40 (high risk)',
  },
  triglycerides: {
    optimal: { max: 150 },
    moderate: { min: 150, max: 199 },
    high: { min: 200 },
    unit: 'mg/dL',
    displayRange: '<150 (optimal), 150-199 (moderate), ≥200 (high)',
  },
  apoB: {
    optimal: { max: 90 },
    moderate: { min: 90, max: 129 },
    high: { min: 130 },
    unit: 'mg/dL',
    targetValue: 90,
    displayRange: '<90 (optimal), 90-129 (moderate), ≥130 (high)',
  },
  nonHDLCholesterol: {
    optimal: { max: 130 },
    moderate: { min: 130, max: 189 },
    high: { min: 190 },
    unit: 'mg/dL',
    displayRange: '<130 (optimal), 130-189 (moderate), ≥190 (high)',
  },

  // METABOLIC
  hba1c: {
    optimal: { max: 5.7 },
    moderate: { min: 5.7, max: 6.4 },
    high: { min: 6.4 },
    unit: '%',
    displayRange: '<5.7 (optimal), 5.7-6.4 (prediabetes), >6.4 (diabetes)',
  },
  glucose: {
    optimal: { min: 65, max: 99 },
    moderate: { min: 100, max: 125 },
    high: { min: 126 },
    unit: 'mg/dL',
    targetValue: 90,
    displayRange: '65-99 (normal), 100-125 (prediabetes), ≥126 (diabetes)',
  },
  estimatedAvgGlucose: {
    optimal: { max: 117 },
    moderate: { min: 117, max: 137 },
    high: { min: 137 },
    unit: 'mg/dL',
    displayRange: '<117 (optimal), 117-137 (moderate), >137 (high)',
  },

  // INFLAMMATION
  hsCRP: {
    optimal: { max: 1.0 },
    moderate: { min: 1.0, max: 3.0 },
    high: { min: 3.0 },
    unit: 'mg/L',
    displayRange: '<1.0 (optimal), 1.0-3.0 (moderate), >3.0 (high)',
  },

  // VITAMINS/SUPPLEMENTS
  vitaminD: {
    optimal: { min: 30.0 },
    moderate: { min: 20.0, max: 29.9 },
    high: { max: 20.0 }, // Also >150 is high, but we'll handle that separately
    unit: 'ng/mL',
    displayRange: '≥30.0 (optimal), 20.0-29.9 (insufficient), <20.0 (deficient)',
  },
  folate: {
    optimal: { min: 5.4 },
    high: { max: 5.4 }, // Deficient below 5.4
    unit: 'ng/mL',
    displayRange: '>5.4 (normal)',
  },
  vitaminB12: {
    optimal: { min: 200, max: 1100 },
    high: { max: 200 }, // Deficient below 200, also high above 1100
    unit: 'pg/mL',
    displayRange: '200-1100 (normal)',
  },

  // THYROID FUNCTION
  tsh: {
    optimal: { min: 0.4, max: 4.0 },
    high: { max: 0.4 }, // Below 0.4 or above 4.0 may indicate thyroid dysfunction
    unit: 'uU/mL',
    displayRange: '0.4-4.0 (normal range)',
  },
  freeT4: {
    optimal: { min: 0.8, max: 1.8 },
    high: { max: 0.8 }, // Below 0.8 or above 1.8 may indicate thyroid dysfunction
    unit: 'ng/dL',
    displayRange: '0.8-1.8 (normal range)',
  },

  // KIDNEY FUNCTION
  creatinine: {
    optimal: { min: 0.60, max: 1.24 },
    high: { min: 1.24 }, // Elevated above 1.24 indicates kidney dysfunction
    unit: 'mg/dL',
    displayRange: '0.60-1.24',
  },
  egfr: {
    optimal: { min: 60 },
    high: { max: 60 }, // Below 60 indicates chronic kidney disease
    unit: 'mL/min/1.73 m²',
    displayRange: '≥60 (normal)',
  },
  bun: {
    optimal: { min: 8, max: 23 },
    high: { min: 23 }, // Elevated above 23 may indicate kidney issues
    unit: 'mg/dL',
    displayRange: '8-23',
  },

  // BODY COMPOSITION
  bmi: {
    optimal: { min: 18.5, max: 24.9 },
    moderate: { min: 25, max: 29.9 },
    high: { min: 30 },
    unit: 'kg/m²',
    displayRange: '18.5-24.9 (normal), 25-29.9 (overweight), ≥30 (obese)',
  },

  // BLOOD PRESSURE
  systolicBP: {
    optimal: { max: 120 },
    moderate: { min: 120, max: 139 },
    high: { min: 140 },
    unit: 'mmHg',
    targetValue: 120,
    displayRange: '<120 (optimal), 120-139 (elevated), ≥140 (high)',
  },
  diastolicBP: {
    optimal: { max: 80 },
    moderate: { min: 80, max: 89 },
    high: { min: 90 },
    unit: 'mmHg',
    targetValue: 80,
    displayRange: '<80 (optimal), 80-89 (elevated), ≥90 (high)',
  },
};

/**
 * Determine risk level based on value and reference range
 */
export function getRiskLevel(
  value: number,
  metricKey: keyof typeof CLEVELAND_HEARTLAB_RANGES,
  options?: { invert?: boolean } // For metrics where higher is better (e.g., HDL)
): 'low' | 'medium' | 'high' {
  const range = CLEVELAND_HEARTLAB_RANGES[metricKey];
  if (!range) return 'low';

  const invert = options?.invert || false;

  // Check high risk
  if (range.high) {
    if (range.high.min !== undefined && value >= range.high.min) {
      return invert ? 'low' : 'high';
    }
    if (range.high.max !== undefined && value <= range.high.max) {
      return invert ? 'low' : 'high';
    }
  }

  // Check moderate risk
  if (range.moderate) {
    const inModerateRange =
      (range.moderate.min === undefined || value >= range.moderate.min) &&
      (range.moderate.max === undefined || value <= range.moderate.max);
    if (inModerateRange) {
      return 'medium';
    }
  }

  // Check optimal range
  if (range.optimal) {
    const inOptimalRange =
      (range.optimal.min === undefined || value >= range.optimal.min) &&
      (range.optimal.max === undefined || value <= range.optimal.max);
    if (inOptimalRange) {
      return invert ? 'high' : 'low';
    }
  }

  // Default to low risk if within optimal or no clear category
  return 'low';
}

/**
 * Get the display string for a reference range
 */
export function getReferenceRangeDisplay(metricKey: keyof typeof CLEVELAND_HEARTLAB_RANGES): string {
  const range = CLEVELAND_HEARTLAB_RANGES[metricKey];
  return range?.displayRange || 'N/A';
}

/**
 * Get the target value for chart display
 */
export function getTargetValue(metricKey: keyof typeof CLEVELAND_HEARTLAB_RANGES): number | undefined {
  return CLEVELAND_HEARTLAB_RANGES[metricKey]?.targetValue;
}
